import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

const ReceivablesWidget = () => {
    const { companyId } = useParams();
    const [loading, setLoading] = useState(true);
    const [agingData, setAgingData] = useState(null);
    const [overdueInvoices, setOverdueInvoices] = useState([]);
    const [reminderStates, setReminderStates] = useState({});

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    useEffect(() => {
        fetchData();
    }, [companyId]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [agingRes, paymentsRes] = await Promise.all([
                axiosInstance.get(getApiUrl(`${companyId}/dashboard/ar-aging`)),
                axiosInstance.get(getApiUrl(`${companyId}/dashboard/upcoming-payments?limit=50`))
            ]);

            if (agingRes.status === 200) {
                setAgingData(agingRes.data);
            }

            if (paymentsRes.status === 200) {
                const payments = paymentsRes.data.payments || [];
                const overdue = payments
                    .filter(p => p.type === 'invoice' && p.daysUntilDue < 0)
                    .sort((a, b) => a.daysUntilDue - b.daysUntilDue)
                    .slice(0, 5);
                setOverdueInvoices(overdue);
            }
        } catch (error) {
            console.error('Error fetching receivables data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendReminder = async (invoice) => {
        const email = invoice.deliveryEmail || invoice.customerEmail;
        if (!email) return;

        setReminderStates(prev => ({ ...prev, [invoice.id]: 'loading' }));

        try {
            await axiosInstance.post(
                getApiUrl(`${companyId}/invoices/${invoice.id}/send-reminder`),
                { email }
            );
            setReminderStates(prev => ({ ...prev, [invoice.id]: 'success' }));
            setTimeout(() => {
                setReminderStates(prev => ({ ...prev, [invoice.id]: null }));
            }, 3000);
        } catch (error) {
            console.error('Error sending reminder:', error);
            setReminderStates(prev => ({ ...prev, [invoice.id]: 'error' }));
            setTimeout(() => {
                setReminderStates(prev => ({ ...prev, [invoice.id]: null }));
            }, 3000);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-section">
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <span style={{ color: 'var(--secondary)', fontSize: '0.875rem' }}>Laddar kundfordringar...</span>
                </div>
            </div>
        );
    }

    if (!agingData) {
        return (
            <div className="dashboard-section">
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--secondary)' }}>
                    Ingen data tillgänglig
                </div>
            </div>
        );
    }

    const notDueBucket = agingData.buckets?.find(b => b.bucket === 'Ej förfallen') || { outstandingAmount: 0, invoiceCount: 0 };
    const overdueBuckets = (agingData.buckets || []).filter(b => b.bucket !== 'Ej förfallen');
    const overdueAmount = overdueBuckets.reduce((sum, b) => sum + b.outstandingAmount, 0);
    const overdueCount = overdueBuckets.reduce((sum, b) => sum + b.invoiceCount, 0);
    const totalAmount = agingData.totalOutstanding || 0;
    const totalCount = (agingData.buckets || []).reduce((sum, b) => sum + b.invoiceCount, 0);

    // AR aging bucket colors (green → red gradient)
    const agingColors = ['#10b981', '#84cc16', '#f59e0b', '#f97316', '#ef4444'];
    const agingBucketLabels = ['Ej förfallen', '1-30d', '31-60d', '61-90d', '>90d'];

    return (
        <div className="dashboard-section">
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
                <div className="dashboard-section-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--primary)' }}>
                        <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9 15H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <path d="M9 11H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </div>
                <h5 className="mb-0 fw-semibold dashboard-section-title">Kundfordringar</h5>
            </div>

            {/* Summary Stats */}
            <div className="dashboard-receivables-stats">
                <div className="dashboard-receivables-stat">
                    <div className="dashboard-status-label">
                        Totalt utestående
                    </div>
                    <div className="dashboard-status-value">
                        {formatCurrency(totalAmount)}
                    </div>
                    <div className="dashboard-summary-label" style={{ fontSize: '0.7rem' }}>
                        {totalCount} fakturor
                    </div>
                </div>

                <div className="dashboard-receivables-stat">
                    <div className="dashboard-status-label">
                        Ej förfallna
                    </div>
                    <div className="dashboard-status-value">
                        {formatCurrency(notDueBucket.outstandingAmount)}
                    </div>
                    <div className="dashboard-summary-label" style={{ fontSize: '0.7rem' }}>
                        {notDueBucket.invoiceCount} fakturor
                    </div>
                </div>

                <div className="dashboard-receivables-stat" style={overdueAmount > 0 ? { background: 'rgba(247, 43, 80, 0.1)', borderColor: 'var(--danger)' } : {}}>
                    <div className="dashboard-status-label" style={overdueAmount > 0 ? { color: 'var(--danger)' } : {}}>
                        Förfallna
                    </div>
                    <div className={overdueAmount > 0 ? 'dashboard-status-value dashboard-status-value--danger' : 'dashboard-status-value'}>
                        {formatCurrency(overdueAmount)}
                    </div>
                    <div className="dashboard-summary-label" style={{ fontSize: '0.7rem', color: overdueAmount > 0 ? 'var(--danger)' : undefined }}>
                        {overdueCount} fakturor
                    </div>
                </div>
            </div>

            {/* AR Aging Bar Visualization */}
            {totalAmount > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                    <div className="dashboard-status-label" style={{ marginBottom: '0.5rem' }}>
                        Åldersfördelning
                    </div>
                    <div style={{
                        display: 'flex',
                        height: '24px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        border: '1px solid var(--border, #d7dae3)'
                    }}>
                        {(agingData.buckets || []).map((bucket, i) => {
                            const percentage = totalAmount > 0 ? (bucket.outstandingAmount / totalAmount) * 100 : 0;
                            if (percentage === 0) return null;
                            return (
                                <div
                                    key={bucket.bucket}
                                    style={{
                                        width: `${percentage}%`,
                                        backgroundColor: agingColors[i],
                                        minWidth: percentage > 0 ? '4px' : 0,
                                        transition: 'width 0.3s ease'
                                    }}
                                    title={`${bucket.bucket}: ${formatCurrency(bucket.outstandingAmount)} (${percentage.toFixed(1)}%)`}
                                />
                            );
                        })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {(agingData.buckets || []).map((bucket, i) => {
                            const percentage = totalAmount > 0 ? (bucket.outstandingAmount / totalAmount) * 100 : 0;
                            return (
                                <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem', color: 'var(--secondary)' }}>
                                    <span style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '2px',
                                        backgroundColor: agingColors[i]
                                    }} />
                                    {agingBucketLabels[i]} ({percentage.toFixed(0)}%)
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Overdue Invoice List */}
            <div style={{ marginTop: '1.5rem' }}>
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '0.75rem' }}>
                    <span className="dashboard-summary-value" style={{ fontSize: '0.875rem' }}>
                        Förfallna fakturor
                    </span>
                    <Link
                        to={`/${companyId}/invoices`}
                        style={{ fontSize: '0.8rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: 500 }}
                    >
                        Visa alla &rarr;
                    </Link>
                </div>

                {overdueInvoices.length === 0 ? (
                    <div style={{
                        padding: '1rem',
                        background: 'var(--rgba-primary-1)',
                        borderRadius: '8px',
                        border: '1px solid var(--rgba-primary-3)',
                        color: 'var(--primary)',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        textAlign: 'center'
                    }}>
                        Inga förfallna fakturor
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {overdueInvoices.map((invoice) => {
                            const daysOverdue = Math.abs(invoice.daysUntilDue);
                            const state = reminderStates[invoice.id];
                            const hasEmail = !!(invoice.deliveryEmail || invoice.customerEmail);

                            return (
                                <div key={invoice.id} className="dashboard-overdue-row">
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <span className="dashboard-summary-value" style={{ fontSize: '0.85rem' }}>
                                            {invoice.entityName}
                                        </span>
                                    </div>
                                    <div className="dashboard-summary-label" style={{ whiteSpace: 'nowrap' }}>
                                        Faktura #{invoice.referenceNumber}
                                    </div>
                                    <div className="dashboard-summary-value" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap', minWidth: '90px', textAlign: 'right' }}>
                                        {formatCurrency(invoice.amount)}
                                    </div>
                                    <div className="dashboard-metric-change negative" style={{
                                        padding: '0.2rem 0.5rem',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {daysOverdue} dagar
                                    </div>
                                    <div style={{ minWidth: '80px', textAlign: 'right' }}>
                                        {state === 'loading' ? (
                                            <button className="dashboard-remind-btn" disabled>
                                                <span className="dashboard-remind-btn__spinner" />
                                            </button>
                                        ) : state === 'success' ? (
                                            <button className="dashboard-remind-btn dashboard-remind-btn--success" disabled>
                                                &#10003; Skickad
                                            </button>
                                        ) : state === 'error' ? (
                                            <button className="dashboard-remind-btn dashboard-remind-btn--error" disabled>
                                                Fel
                                            </button>
                                        ) : (
                                            <button
                                                className="dashboard-remind-btn"
                                                onClick={() => handleSendReminder(invoice)}
                                                disabled={!hasEmail}
                                                title={hasEmail ? 'Skicka betalningspåminnelse' : 'Ingen e-postadress'}
                                            >
                                                Påminn
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {overdueInvoices.length > 0 && overdueCount > 5 && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--secondary)', fontStyle: 'italic' }}>
                        Visar de {overdueInvoices.length} mest förfallna av totalt {overdueCount} förfallna fakturor.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReceivablesWidget;
